import React, { useState, useEffect, useRef, useCallback } from 'react'
import useWebSocket, { ReadyState } from 'react-use-websocket'
import { useAudioRecorder, encodeInt16 } from '../hooks/useAudioRecorder/useAudioRecorder'
import { usePlaybackAudioStream } from '../hooks/usePlaybackAudioStream/usePlaybackAudioStream'
import BargeInListener from './BargeInListener'
import VoiceAssistantWidget, { VoiceAssistantState } from './VoiceAssistantWidget'
import { navigate } from 'gatsby'
import './SocketBox.scss'

const AI_VOICE_ASSISTANT_WS_URL = process.env.GATSBY_AI_VOICE_ASSISTANT_WS_URL
const SAMPLE_RATE = 16000 // 16 kHz for speech

const AUDIO_CONSTRAINTS: MediaStreamConstraints = {
    audio: { echoCancellation: true, noiseSuppression: true, sampleRate: SAMPLE_RATE },
}

// 'interrupted' covers the window between detecting a barge-in and having the
// full interrupting statement
enum AgentState {
    Idle = 'idle',
    Connecting = 'connecting',
    Listening = 'listening',
    Thinking = 'thinking',
    Speaking = 'speaking',
    Interrupted = 'interrupted',
    Error = 'error',
}

interface NavigationMessage {
    type: string
    path: string
    sectionId?: string
}

const SocketBox: React.FC = () => {
    const [agentState, setAgentState] = useState<AgentState>(AgentState.Idle)
    const [socketUrl, setSocketUrl] = useState<string | null>(null)
    const [isUserMuted, setIsUserMuted] = useState(false)
    const pendingRecordRef = useRef(false)
    const preObtainedStreamRef = useRef<MediaStream | null>(null)
    const micStreamRef = useRef<MediaStream | null>(null)
    const isStartingRef = useRef(false)
    const navigationRef = useRef<NavigationMessage | null>(null)
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const llmFallbackTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const awaitingNewTurnRef = useRef(false)
    const [triggerNavigate, setTriggerNavigate] = useState(false)

    const { isPlayingAudio, handleSpeechMessage, stopPlayback } = usePlaybackAudioStream()
    const isPlayingAudioRef = useRef(isPlayingAudio)
    isPlayingAudioRef.current = isPlayingAudio

    const agentStateRef = useRef(agentState)
    agentStateRef.current = agentState

    const isUserMutedRef = useRef(isUserMuted)
    isUserMutedRef.current = isUserMuted

    const getMicStream = useCallback(() => Promise.resolve(micStreamRef.current as MediaStream), [])

    const { sendMessage, readyState, getWebSocket } = useWebSocket(socketUrl, {
        onOpen: () => {},
        onClose: (event) => {
            // A socket we closed ourselves can report its close after a new session has started
            if (event.target !== getWebSocket()) return
            onDisconnectRef.current()
        },
        onError: (event) => {
            console.error('[SocketBox] WebSocket error', event)
        },
        onMessage: (message) => {
            try {
                const data = JSON.parse(message.data)
                if (data.type === 'speech') {
                    if (awaitingNewTurnRef.current) {
                        console.debug('[SocketBox] Dropping stale speech chunk after interrupt')
                        return
                    }
                    handleSpeechMessage(data)
                } else if (data.type === 'thinking') {
                    awaitingNewTurnRef.current = false
                    setAgentState(AgentState.Thinking)
                } else if (data.type === 'navigation') {
                    navigationRef.current = data
                    setTriggerNavigate((prev) => !prev)
                } else if (data.type === 'llm_response') {
                    if (llmFallbackTimeoutRef.current) clearTimeout(llmFallbackTimeoutRef.current)
                    llmFallbackTimeoutRef.current = setTimeout(() => {
                        setAgentState((prev) =>
                            prev === AgentState.Thinking ? AgentState.Listening : prev,
                        )
                    }, 1000)
                }
            } catch (err) {
                console.error('[SocketBox] Failed to parse message data', err)
            }
        },
        shouldReconnect: () => false,
    })

    const { isRecording, handleStartRecording, handleStopRecording, setMuted } = useAudioRecorder({
        sendMessage,
        readyState,
    })

    const handleStartRecordingRef = useRef(handleStartRecording)
    handleStartRecordingRef.current = handleStartRecording

    useEffect(() => {
        if (readyState === ReadyState.OPEN && pendingRecordRef.current) {
            pendingRecordRef.current = false
            handleStartRecordingRef.current(preObtainedStreamRef.current ?? undefined)
            preObtainedStreamRef.current = null
        }
    }, [readyState])

    useEffect(() => {
        if (isRecording) {
            setAgentState((prev) => (prev === AgentState.Connecting ? prev : AgentState.Listening))
        }
    }, [isRecording])

    useEffect(() => {
        if (isPlayingAudio) {
            if (llmFallbackTimeoutRef.current) {
                clearTimeout(llmFallbackTimeoutRef.current)
                llmFallbackTimeoutRef.current = null
            }
            setAgentState(AgentState.Speaking)
        } else {
            setAgentState((prev) => (prev === AgentState.Speaking ? AgentState.Listening : prev))
        }
    }, [isPlayingAudio])

    useEffect(() => {
        if (navigationRef.current) {
            const { path, sectionId } = navigationRef.current
            if (window.location.pathname !== path) {
                navigate(path)
            }
            if (sectionId) {
                if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current)
                }
                scrollTimeoutRef.current = setTimeout(() => {
                    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
                }, 1000)
            }
        }
    }, [triggerNavigate])

    useEffect(() => {
        setMuted(
            isUserMuted ||
                agentState === AgentState.Thinking ||
                agentState === AgentState.Speaking ||
                agentState === AgentState.Interrupted,
        )
    }, [agentState, isUserMuted])

    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
            if (llmFallbackTimeoutRef.current) clearTimeout(llmFallbackTimeoutRef.current)
            micStreamRef.current?.getTracks().forEach((track) => track.stop())
        }
    }, [])

    const interruptAgent = () => {
        stopPlayback()
        awaitingNewTurnRef.current = true
        sendMessage(JSON.stringify({ type: 'interrupt' }))
        if (llmFallbackTimeoutRef.current) {
            clearTimeout(llmFallbackTimeoutRef.current)
            llmFallbackTimeoutRef.current = null
        }
    }

    const handleBargeIn = () => {
        if (isUserMutedRef.current) return
        if (
            agentStateRef.current !== AgentState.Speaking &&
            agentStateRef.current !== AgentState.Thinking
        )
            return

        console.info('[SocketBox] Barge-in detected, interrupting agent')
        interruptAgent()
        setAgentState(AgentState.Interrupted)
    }

    const handleBargeInAudio = (audio: Float32Array) => {
        if (agentStateRef.current !== AgentState.Interrupted) return

        try {
            sendMessage(
                JSON.stringify({
                    type: 'audio',
                    sampleRate: SAMPLE_RATE,
                    data: encodeInt16(audio),
                }),
            )
            console.debug(`[SocketBox] Sent barge-in utterance (${audio.length} samples)`)
        } catch (err) {
            console.error('[SocketBox] Failed to send barge-in utterance', err)
        }
        setAgentState(AgentState.Listening)
    }

    const startSession = async () => {
        // agentState stays Idle while the permission prompt is open, so guard repeat clicks
        if (isStartingRef.current) return
        isStartingRef.current = true
        try {
            const stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS)
            preObtainedStreamRef.current = stream
            micStreamRef.current = stream
            pendingRecordRef.current = true
            setAgentState(AgentState.Connecting)
            setSocketUrl(AI_VOICE_ASSISTANT_WS_URL || null)
        } catch (err) {
            console.error('[SocketBox] Microphone permission denied', err)
            setAgentState(AgentState.Error)
        } finally {
            isStartingRef.current = false
        }
    }

    const onDisconnect = () => {
        if (isRecording) handleStopRecording()
        stopPlayback()
        micStreamRef.current?.getTracks().forEach((track) => track.stop())
        preObtainedStreamRef.current = null
        micStreamRef.current = null
        awaitingNewTurnRef.current = false
        pendingRecordRef.current = false
        setSocketUrl(null)
        setIsUserMuted(false)
        setAgentState(AgentState.Idle)
    }

    const getDisplayState = (): VoiceAssistantState => {
        switch (agentState) {
            case AgentState.Connecting:
                return 'connecting'
            case AgentState.Listening:
            case AgentState.Interrupted:
                return isUserMuted ? 'muted' : 'listening'
            case AgentState.Thinking:
                return 'thinking'
            case AgentState.Speaking:
                return 'speaking'
            case AgentState.Error:
                return 'error'
            default:
                return 'idle'
        }
    }
    const displayState = getDisplayState()

    const isActive = agentState !== AgentState.Idle
    // Error has no open socket or mic stream for the barge-in VAD to use
    const isSessionLive = isActive && agentState !== AgentState.Error

    const onOrbClick = () => {
        if (agentState === AgentState.Idle || agentState === AgentState.Error) {
            startSession()
        } else if (displayState === 'muted') {
            setIsUserMuted(false)
        } else if (agentState === AgentState.Speaking) {
            interruptAgent()
            setAgentState(AgentState.Listening)
        } else {
            onDisconnect()
        }
    }

    const onDisconnectRef = useRef(onDisconnect)
    onDisconnectRef.current = onDisconnect

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape' && isActive) onDisconnect()
    }

    return (
        <div className="socketbox-widget" onKeyDown={onKeyDown}>
            <VoiceAssistantWidget
                state={displayState}
                isMuted={isUserMuted}
                onOrbClick={onOrbClick}
                onMuteToggle={() => setIsUserMuted((prev) => !prev)}
                onClose={onDisconnect}
            />
            {isSessionLive && (
                <BargeInListener
                    getStream={getMicStream}
                    onBargeIn={handleBargeIn}
                    onBargeInAudio={handleBargeInAudio}
                />
            )}
        </div>
    )
}

export default SocketBox
